/**
 * Page-local URL params for PriorityOutreachPage's four list-affecting pieces of state that were
 * NOT surviving back-navigation or a refresh: bucket, returnType, pageSize and skip. All four
 * drive the query key directly (see PriorityOutreachPage.tsx's useQuery call), unlike the
 * row-selection state deliberately excluded on other pages in this sweep.
 *
 * Conventions (mirroring useTaxReturnsScopeParams.ts / useClientOptOutsScopeParams.ts /
 * useInHouseClientsScopeParams.ts):
 * - Each param is present only when it differs from the page's own default; an invalid or
 *   out-of-range value falls back to that default rather than leaking bad state (NaN, a negative
 *   offset, an unrecognised bucket).
 * - Changing bucket, returnType or pageSize resets skip to 0, matching the page's existing
 *   changeBucket/changeReturnType/changePageSize behavior (you don't want page 4 of a filter you
 *   just switched into).
 * - Mutations replace history (never push), matching the other hooks in this sweep.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PriorityBucket } from '../types';

const DEFAULT_BUCKET: PriorityBucket = 'two_year_no_current';
const DEFAULT_PAGE_SIZE = 50;
const VALID_BUCKETS: readonly PriorityBucket[] = [
  'two_year_no_current', 'one_year_no_current', 'extension_current_pending',
];

export interface PriorityOutreachScopeParams {
  bucket: PriorityBucket;
  returnType: string;
  pageSize: number;
  skip: number;
  setBucket: (bucket: PriorityBucket) => void;
  setReturnType: (returnType: string) => void;
  setPageSize: (pageSize: number) => void;
  setSkip: (skip: number) => void;
}

export function usePriorityOutreachScopeParams(): PriorityOutreachScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const bucket = useMemo<PriorityBucket>(() => {
    const raw = searchParams.get('bucket');
    return raw && (VALID_BUCKETS as string[]).includes(raw) ? (raw as PriorityBucket) : DEFAULT_BUCKET;
  }, [searchParams]);

  const returnType = searchParams.get('returnType') || '';

  const pageSize = useMemo(() => {
    const raw = searchParams.get('pageSize');
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PAGE_SIZE;
  }, [searchParams]);

  const skip = useMemo(() => {
    const raw = searchParams.get('skip');
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }, [searchParams]);

  /** Replace history (never push), via the functional form — safe against a stale closure. */
  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setBucket = useCallback(
    (next: PriorityBucket) => {
      update((params) => {
        if (next === DEFAULT_BUCKET) params.delete('bucket');
        else params.set('bucket', next);
        params.delete('skip');
      });
    },
    [update]
  );

  const setReturnType = useCallback(
    (next: string) => {
      update((params) => {
        if (!next) params.delete('returnType');
        else params.set('returnType', next);
        params.delete('skip');
      });
    },
    [update]
  );

  const setPageSize = useCallback(
    (next: number) => {
      update((params) => {
        if (next === DEFAULT_PAGE_SIZE) params.delete('pageSize');
        else params.set('pageSize', String(next));
        params.delete('skip');
      });
    },
    [update]
  );

  const setSkip = useCallback(
    (next: number) => {
      update((params) => {
        if (next === 0) params.delete('skip');
        else params.set('skip', String(next));
      });
    },
    [update]
  );

  return { bucket, returnType, pageSize, skip, setBucket, setReturnType, setPageSize, setSkip };
}
